/**
 * Pure colour / composite / gradient maths for the theme contract.
 *
 * theme-customization A1. This module knows NOTHING about tokens, roles or pairs — it takes
 * six-digit hex strings and numbers and returns numbers. That separation is deliberate: the
 * web instrument (`apps/web/src/styles/__tests__/contrast-registry.ts`) carries its own
 * independent copy of the same WCAG maths, and the two are each other's second opinion. This
 * file must therefore never import from web, and web must never import this file into its
 * measurement path — `contrast.test.ts` pins independent vectors from the WCAG 2.1 text so
 * agreement between the two copies is evidence rather than a tautology.
 *
 * Every ratio returned here is UNROUNDED. Rounding is a display concern: `truncateRatio`
 * exists for messages and is never used in a comparison.
 */

/** WCAG 2.1 1.4.3 — normal-size text. */
export const AA_TEXT = 4.5;

/** WCAG 2.1 1.4.11 — focus indicators, UI component boundaries and graphical objects. */
export const AA_NON_TEXT = 3;

/** A validated six-digit hex colour, not arbitrary CSS. */
export const HEX_COLOUR_PATTERN = /^#[0-9a-fA-F]{6}$/;

export type Rgb = { r: number; g: number; b: number };

/**
 * `#aabbcc` -> `#AABBCC`, or `null` when the input is not a whole six-digit hex.
 *
 * Three- and eight-digit forms, `rgb()`, `hsl()`, `color-mix()`, named colours and anything
 * carrying whitespace are all rejected: the storage/API contract is six-digit hex only, so an
 * "almost" value must fail rather than be coerced into something that paints.
 */
export function normalizeHexColour(value: string): string | null {
  if (typeof value !== 'string') return null;
  if (!HEX_COLOUR_PATTERN.test(value)) return null;
  return value.toUpperCase();
}

/** Throws on anything `normalizeHexColour` rejects. */
export function parseHexColour(hex: string): Rgb {
  const normalized = normalizeHexColour(hex);
  if (normalized === null) throw new Error(`not a six-digit hex colour: ${JSON.stringify(hex)}`);
  const n = Number.parseInt(normalized.slice(1), 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

function channelToHex(value: number): string {
  const clamped = Math.min(255, Math.max(0, Math.round(value)));
  return clamped.toString(16).padStart(2, '0');
}

export function rgbToHex(rgb: Rgb): string {
  return `#${channelToHex(rgb.r)}${channelToHex(rgb.g)}${channelToHex(rgb.b)}`.toUpperCase();
}

/**
 * The sRGB transfer function's linear/power breakpoint, in 0-255 channel units.
 * WCAG 2.1 uses `s <= 0.03928`, so the breakpoint sits at `0.03928 * 255`.
 */
export const SRGB_BREAKPOINT_CHANNEL = 0.03928 * 255;

function linearize(channel: number): number {
  const s = channel / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** WCAG 2.1 relative luminance. Accepts fractional channels (gradient interiors). */
export function relativeLuminance(rgb: Rgb): number {
  return 0.2126 * linearize(rgb.r) + 0.7152 * linearize(rgb.g) + 0.0722 * linearize(rgb.b);
}

function ratioFromLuminance(a: number, b: number): number {
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

/** WCAG 2.1 contrast ratio between two OPAQUE six-digit hex colours. Unrounded. */
export function contrastRatio(a: string, b: string): number {
  return ratioFromLuminance(
    relativeLuminance(parseHexColour(a)),
    relativeLuminance(parseHexColour(b)),
  );
}

/**
 * Flatten `foreground` at `alpha` over an OPAQUE `background` — what `bg-primary/90` paints.
 *
 * Channel rounding matches the web instrument's `composite` exactly (`Math.round`, uppercase
 * output) so a composited operand resolves to the same hex on both sides of the contract.
 */
export function compositeHex(foreground: string, alpha: number, background: string): string {
  if (!Number.isFinite(alpha) || alpha < 0 || alpha > 1) {
    throw new Error(`composite alpha must be within [0, 1]: ${String(alpha)}`);
  }
  const f = parseHexColour(foreground);
  const b = parseHexColour(background);
  return rgbToHex({
    r: Math.round(f.r * alpha + b.r * (1 - alpha)),
    g: Math.round(f.g * alpha + b.g * (1 - alpha)),
    b: Math.round(f.b * alpha + b.b * (1 - alpha)),
  });
}

/** Unrounded sRGB channel interpolation at `t` in [0, 1]. */
function interpolate(from: Rgb, to: Rgb, t: number): Rgb {
  return {
    r: from.r + (to.r - from.r) * t,
    g: from.g + (to.g - from.g) * t,
    b: from.b + (to.b - from.b) * t,
  };
}

const GRADIENT_GRID_STEPS = 4096;
const GOLDEN_SECTION_ITERATIONS = 200;
const GOLDEN_RATIO_INVERSE = (Math.sqrt(5) - 1) / 2;

export type GradientMinimum = {
  /** The worst (lowest) contrast ratio anywhere along the interpolation. Unrounded. */
  ratio: number;
  /** The interpolated colour at the worst point, rounded for reporting. */
  background: string;
  /** Position of the worst point along the ramp, in [0, 1]. */
  at: number;
};

/**
 * The MINIMUM contrast of `foreground` against a two-stop sRGB gradient from `from` to `to`.
 *
 * Endpoints alone are not sufficient once both stops are editable roles. The ratio, viewed as
 * a function of the interpolated luminance, is a V with its floor where the background's
 * luminance equals the foreground's, so the minimum can sit strictly inside the ramp — and the
 * luminance itself is not monotone in `t` when the three channels move in different directions
 * (the transfer function's linear/power breakpoint adds a kink per channel on top of that).
 *
 * Method, stated so it can be checked rather than trusted:
 *  1. Seed the candidate set with both endpoints and every `t` at which a channel crosses the
 *     sRGB breakpoint (`SRGB_BREAKPOINT_CHANNEL`) — the only points where the luminance is
 *     non-smooth.
 *  2. Add a uniform grid of `GRADIENT_GRID_STEPS` samples, which brackets every interior
 *     stationary point and the luminance crossing to within one grid step.
 *  3. Golden-section refine inside the bracketing interval around the best sample. The ratio is
 *     continuous and, within one grid step of its minimum, unimodal, so this converges to
 *     machine precision.
 */
export function gradientMinimumContrast(
  foreground: string,
  from: string,
  to: string,
): GradientMinimum {
  const fgLuminance = relativeLuminance(parseHexColour(foreground));
  const a = parseHexColour(from);
  const b = parseHexColour(to);

  const ratioAt = (t: number): number =>
    ratioFromLuminance(fgLuminance, relativeLuminance(interpolate(a, b, t)));

  const candidates = new Set<number>([0, 1]);
  for (const [start, end] of [
    [a.r, b.r],
    [a.g, b.g],
    [a.b, b.b],
  ] as const) {
    if (start === end) continue;
    const t = (SRGB_BREAKPOINT_CHANNEL - start) / (end - start);
    if (t > 0 && t < 1) candidates.add(t);
  }
  for (let i = 0; i <= GRADIENT_GRID_STEPS; i += 1) candidates.add(i / GRADIENT_GRID_STEPS);

  const grid = [...candidates].sort((x, y) => x - y);
  let bestIndex = 0;
  let best = ratioAt(grid[0] as number);
  for (let i = 1; i < grid.length; i += 1) {
    const value = ratioAt(grid[i] as number);
    if (value < best) {
      best = value;
      bestIndex = i;
    }
  }

  let lo = grid[Math.max(0, bestIndex - 1)] as number;
  let hi = grid[Math.min(grid.length - 1, bestIndex + 1)] as number;
  let x1 = hi - GOLDEN_RATIO_INVERSE * (hi - lo);
  let x2 = lo + GOLDEN_RATIO_INVERSE * (hi - lo);
  let f1 = ratioAt(x1);
  let f2 = ratioAt(x2);
  for (let i = 0; i < GOLDEN_SECTION_ITERATIONS && hi - lo > Number.EPSILON; i += 1) {
    if (f1 < f2) {
      hi = x2;
      x2 = x1;
      f2 = f1;
      x1 = hi - GOLDEN_RATIO_INVERSE * (hi - lo);
      f1 = ratioAt(x1);
    } else {
      lo = x1;
      x1 = x2;
      f1 = f2;
      x2 = lo + GOLDEN_RATIO_INVERSE * (hi - lo);
      f2 = ratioAt(x2);
    }
  }

  const refinedAt = f1 < f2 ? x1 : x2;
  const refined = Math.min(f1, f2);
  const at = refined < best ? refinedAt : (grid[bestIndex] as number);
  const ratio = Math.min(refined, best);
  return { ratio, background: rgbToHex(interpolate(a, b, at)), at };
}

/**
 * Truncated toward zero to 2dp — the project convention for QUOTING a ratio. Never rounded up,
 * and never used in a comparison: comparisons use the unrounded value.
 */
export function truncateRatio(ratio: number): string {
  return (Math.floor(ratio * 100) / 100).toFixed(2);
}
